// src/screens/YourTeam.js
import React, { useEffect, useState, useCallback, useMemo } from 'react'; // Added useMemo
import {
  View, Text, StyleSheet, ScrollView, ActivityIndicator,
  TouchableOpacity, Modal, TextInput, Alert, SafeAreaView
} from 'react-native';
// NEW: Import useNavigation to make event list tappable
import { useNavigation } from '@react-navigation/native'; 

import { onAuthStateChanged } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../../firebaseConfig';
import { useError } from '../components/ErrorContext';
// Team memberships (evergreen 3.5b): teamEverLaunched keys the member line's
// second sentence (static copy only; the server decides every control).
import useMembershipsLive from '../hooks/useMembershipsLive';

import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import { Ionicons } from '@expo/vector-icons';
// Squad Streaming section (Team Streams v2: a squad IS the registered team;
// streaming controls render here too, roster management stays on this tab)
import SquadManagePanel, { useTeamStreamsVisible, SquadLinkPrompt } from '../components/SquadManagePanel';
// Economy flag (settings/economy.critsEnabled): the Crit stat card renders
// only while Crits are on.
import useCritsEnabled from '../hooks/useCritsEnabled';

// Helper-driven API (callables + shaping)
import {
  loadTeamBundle,
  createTeam,
  joinTeamByCode,
  leaveTeam,
  transferCaptain,
  disbandTeam,
  saveTeam,
  regenerateInvite,
  removeMember,
  isCaptain,
  usd,
  tsToMs,
  // NEW: We will need a new helper to fetch team-specific events
  // We can add this to 'teamHelper.js' later.
  // For now, we'll write the logic in `refresh`
  // and assume `loadTeamEvents` will be created.
} from '../utils/teamHelper';
import { collection, collectionGroup, getDocs, getDoc, doc, query, where, limit } from 'firebase/firestore'; // Added doc, limit

const GOLD = '#FCD000';

/* =========================
 * Team memberships (evergreen 3.5b, design 13.3)
 * The row and lines below render only while getMyStreamInfo's
 * teamMembershipEntry.show is true (the team sell gate, or team memberships
 * ever launched). Roster copy: money-free, never an amount or a share.
 * ========================= */
const TM_DISBAND_LINE = "If viewers have joined your team's membership, their memberships stop renewing and end at the end of the month each member paid for. Saving the team later does not restart them; each member can choose to keep theirs.";
const TM_ONE_LEFT_LINE = "If viewers have joined your team's membership, this leaves one person on the roster, so their memberships stop renewing and end at the end of the month each member paid for.";
const TM_SAVED_LINE = 'Team saved. Members whose team memberships were set to end can keep them from Your memberships.';
const TM_MEMBER_LINE = 'Your captain manages team memberships.';
const TM_MEMBER_SHARE_LINE = "Members who are verified and stream linked to the squad for at least 30 minutes in a month share that month's team memberships.";

// The captain's state line under the "Team memberships" row, keyed on the
// entry's joinShown (what the public team page shows viewers), then offering.
export function teamMembershipStateLine(entry) {
  if (entry?.joinShown === true) return "Viewers can join your team's membership from its team page.";
  if (entry?.offering === true) return "New viewers can't join right now. See Team memberships.";
  return 'Set up in Team memberships.';
}

/* =========================
 * Modal: Create Team
 * (This component is UNCHANGED)
 * ========================= */
function CreateTeamModal({ visible, onClose, onDone }) {
  // ... (all existing code for CreateTeamModal is unchanged) ...
  const { showError } = useError();
  const [name, setName] = useState('');
  const [tag, setTag] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!visible) { setName(''); setTag(''); setBusy(false); }
  }, [visible]);

  const submit = async () => {
    const teamName = String(name || '').trim();
    const teamTag = String(tag || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 5);
    if (teamName.length < 3) return showError('Name too short', 'At least 3 characters.');
    setBusy(true);
    try {
      await createTeam({ name: teamName, tag: teamTag || null });
      onDone?.();
      onClose?.();
      showError('Team created', 'You are the team captain.');
    } catch (e) {
      const msg = (e?.message || '').toLowerCase();
      if (msg.includes('name is already')) showError('Name taken', 'That team name is already in use.');
      else showError('Create failed', e?.message || 'Unable to create team.');
    } finally { setBusy(false); }
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.modalBackdrop}>
        <View style={styles.modalCard}>
          <Text style={styles.modalTitle}>Create Team</Text>
          <TextInput
            style={styles.input}
            placeholder="Team name"
            placeholderTextColor="#777"
            value={name}
            onChangeText={setName}
            maxLength={40}
          />
          <TextInput
            style={styles.input}
            placeholder="Tag (1-5 chars, optional)"
            placeholderTextColor="#777"
            value={tag}
            onChangeText={setTag}
            autoCapitalize="characters"
            maxLength={5}
          />
          <View style={styles.rowGap}>
            <TouchableOpacity style={[styles.btn, styles.btnSecondary]} onPress={onClose} disabled={busy}>
              <Text style={styles.btnSecondaryText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.btn, styles.btnPrimary]} onPress={submit} disabled={busy}>
              <Text style={styles.btnPrimaryText}>{busy ? 'Creating...' : 'Create'}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

/* =========================
 * Modal: Join Team
 * (This component is UNCHANGED)
 * ========================= */
function JoinTeamModal({ visible, onClose, onDone }) {
  // ... (all existing code for JoinTeamModal is unchanged) ...
  const { showError } = useError();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!visible) { setCode(''); setBusy(false); }
  }, [visible]);

  const submit = async () => {
    const inviteCode = String(code || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (inviteCode.length < 4) return showError('Invalid code', 'Please check and try again.');
    setBusy(true);
    try {
      await joinTeamByCode(inviteCode);
      onDone?.();
      onClose?.();
      showError('Joined', 'Welcome to the team.');
    } catch (e) {
      showError('Join failed', e?.message || 'Unable to join.');
    } finally { setBusy(false); }
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.modalBackdrop}>
        <View style={styles.modalCard}>
          <Text style={styles.modalTitle}>Join Team</Text>
          <TextInput
            style={styles.input}
            placeholder="Invite code"
            placeholderTextColor="#777"
            value={code}
            onChangeText={setCode}
            autoCapitalize="characters"
            maxLength={12}
          />
          <View style={styles.rowGap}>
            <TouchableOpacity style={[styles.btn, styles.btnSecondary]} onPress={onClose} disabled={busy}>
              <Text style={styles.btnSecondaryText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.btn, styles.btnPrimary]} onPress={submit} disabled={busy}>
              <Text style={styles.btnPrimaryText}>{busy ? 'Joining...' : 'Join'}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

/* =========================
 * Modal: Transfer Captain
 * (This component is UNCHANGED)
 * ========================= */
function TransferCaptainModal({ visible, onClose, team, onDone }) {
  // ... (all existing code is unchanged) ...
  const { showError } = useError();
  const [targetUid, setTargetUid] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!visible) { setTargetUid(''); setBusy(false); } }, [visible]);

  const eligible = (team?.memberProfiles || [])
    .filter(m => m.uid !== team.ownerId)
    .sort((a, b) => a.userName.localeCompare(b.userName));

  const submit = async () => {
    if (!targetUid) return;
    setBusy(true);
    try {
      await transferCaptain(team.id, targetUid);
      onDone?.();
      onClose?.();
      showError('Captain transferred', 'New captain assigned.');
    } catch (e) {
      showError('Transfer failed', e?.message || 'Unable to transfer.');
    } finally { setBusy(false); }
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.modalBackdrop}>
        <View style={styles.modalCard}>
          <Text style={styles.modalTitle}>Transfer Captain</Text>
          <View style={styles.memberList}>
            {eligible.length === 0 ? (
                <Text style={styles.muted}>No eligible members to transfer to.</Text>
            ) : eligible.map(m => {
                const on = targetUid === m.uid;
                return (
                    <TouchableOpacity
                    key={m.uid}
                    style={[styles.memberOption, on && styles.memberOptionOn]}
                    onPress={() => setTargetUid(m.uid)}
                    activeOpacity={0.8}
                    hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
                    >
                    <Text style={[styles.memberText, on && styles.memberTextOn]} numberOfLines={1}>
                        {on ? '* ' : 'o '}{m.userName}
                    </Text>
                    </TouchableOpacity>
                );
                })
            }
          </View>
          <View style={styles.rowGap}>
            <TouchableOpacity style={[styles.btn, styles.btnSecondary]} onPress={onClose} disabled={busy}>
              <Text style={styles.btnSecondaryText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.btn, styles.btnBrand]} onPress={submit} disabled={busy || !targetUid}>
              <Text style={styles.btnBrandText}>{busy ? 'Transferring...' : 'Transfer Captain'}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

/* =========================
 * Modal: Disband Team
 * (This component is UNCHANGED)
 * ========================= */
function DisbandTeamModal({ visible, onClose, team, onDone, teamMembershipsShown }) {
  // ... (all existing code is unchanged) ...
  const { showError } = useError();
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!visible) setBusy(false); }, [visible]);

  const disband = async () => {
    setBusy(true);
    try {
      await disbandTeam(team.id, { graceful: true });
      onDone?.();
      onClose?.();
      showError('Team disbanded', 'The team has been absolved.');
    } catch (e) {
      showError('Disband failed', e?.message || 'Unable to disband.');
    } finally { setBusy(false); }
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.modalBackdrop}>
        <View style={styles.modalCard}>
          <Text style={styles.modalTitle}>Absolve Team</Text>
          <Text style={styles.muted}>
            You may want to transfer captain first so others can keep the team. If a grace period is active, members can "save" the team.
          </Text>
          {teamMembershipsShown ? <Text style={styles.muted}>{TM_DISBAND_LINE}</Text> : null}
          <View style={styles.rowGap}>
            <TouchableOpacity style={[styles.btn, styles.btnSecondary]} onPress={onClose} disabled={busy}>
              <Text style={styles.btnSecondaryText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.btn, styles.btnDanger]} onPress={disband} disabled={busy}>
              <Text style={styles.btnDangerText}>{busy ? 'Working...' : 'Disband Team'}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

/* =========================
 * Modal: Generic Confirmation
 * (This component is UNCHANGED)
 * ========================= */
function ConfirmationModal({ visible, title, message, confirmText, onCancel, onConfirm, busy }) {
  // ... (all existing code is unchanged) ...
  const { showError } = useError();
  const confirmButtonStyle = confirmText === 'Leave' || confirmText === 'Remove' ? styles.btnDanger : styles.btnPrimary;
  const confirmButtonTextStyle = confirmText === 'Leave' || confirmText === 'Remove' ? styles.btnDangerText : styles.btnPrimaryText;

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onCancel}>
      <View style={styles.modalBackdrop}>
        <View style={styles.modalCard}>
          <Text style={styles.modalTitle}>{title}</Text>
          {message && <Text style={styles.muted}>{message}</Text>}
          <View style={styles.rowGap}>
            <TouchableOpacity style={[styles.btn, styles.btnSecondary]} onPress={onCancel} disabled={busy}>
              <Text style={styles.btnSecondaryText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.btn, confirmButtonStyle]} onPress={onConfirm} disabled={busy}>
              <Text style={confirmButtonTextStyle}>{busy ? 'Working...' : confirmText}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

/* =========================
 * Component: TeamCard
 * (This component is MODIFIED)
 * ========================= */
// MODIFIED: Added 'teamEvents' prop
function TeamCard({ team, meUid, refresh, stats, nextEvents, teamEvents, teamMembershipEntry }) {
  const { showError } = useError();
  // NEW: Get navigation
  const navigation = useNavigation();
  // Squad Streaming visibility (settings/streams.teamStreamsEnabled flag,
  // admins exempt) - the section below renders only when the feature is on.
  const squadStreamsVisible = useTeamStreamsVisible();
  const critsEnabled = useCritsEnabled();
  const { teamEverLaunched } = useMembershipsLive();
  const iAmOwner = isCaptain(team, meUid);
  const members = team.memberProfiles || [];
  const captain = members.find(m => m.uid === team.ownerId);
  // Team memberships (evergreen 3.5b): the entry drives the row below the
  // Members list and the membership sentences in the roster confirms.
  const tmEntry = teamMembershipEntry?.show === true ? teamMembershipEntry : null;
  const tmShown = !!tmEntry;
  const rosterCount = Array.isArray(team.memberIds) ? team.memberIds.length : members.length;
  const tmOneLeft = tmShown && rosterCount === 2;
  // ... (existing state: showTransfer, showDisband, busy, confirmAction) ...
  const [showTransfer, setShowTransfer] = useState(false);
  const [showDisband, setShowDisband] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  // ... (existing functions: doLeave, doKick, handleConfirm, doRegenInvite, doSave) ...
  // All these functions remain 100% UNCHANGED.
  const doLeave = () => {
    if (iAmOwner) return showError('Captain cannot leave', 'Transfer captain or disband.');
    setConfirmAction({
      action: 'leave',
      title: 'Leave Team',
      message: tmOneLeft
        ? `Are you sure you want to leave this team?\n\n${TM_ONE_LEFT_LINE}`
        : 'Are you sure you want to leave this team?',
      confirmText: 'Leave',
    });
  };
  
  const doKick = (memberUid, memberName) => {
    if (!iAmOwner || memberUid === team.ownerId) return;
    setConfirmAction({
      action: 'kick',
      memberUid, // Store the member's ID for the handler
      title: 'Remove Member',
      message: tmOneLeft
        ? `Are you sure you want to remove ${memberName} from the team?\n\n${TM_ONE_LEFT_LINE}`
        : `Are you sure you want to remove ${memberName} from the team?`,
      confirmText: 'Remove',
    });
  };

  const handleConfirm = async () => {
    if (!confirmAction || busy) return;

    setBusy(true);
    const { action, memberUid } = confirmAction;

    try {
      if (action === 'leave') {
        await leaveTeam(team.id);
        showError('Left team', 'You are no longer a member.');
      } else if (action === 'kick') {
        await removeMember(team.id, memberUid);
        showError('Success', 'The member has been removed.');
      }
      
      setConfirmAction(null);
      await refresh();

    } catch (e) {
      showError(action === 'leave' ? 'Leave failed' : 'Remove failed', e?.message || 'Unknown error.');
    } finally {
      setBusy(false);
      if (action) setConfirmAction(null);
    }
  };

  const doRegenInvite = async () => {
    try {
      const res = await regenerateInvite(team.id);
      await refresh();
      const newCode = res?.inviteCode;
      if (newCode) showError('New invite code', newCode);
    } catch (e) {
      showError('Error', e?.message || 'Could not regenerate code.');
    }
  };

  const doSave = async () => {
    try { await saveTeam(team.id); await refresh(); showError('Saved', tmShown ? TM_SAVED_LINE : 'Team disbanding cancelled.'); }
    catch (_) {}
  };


  // NEW: Helper to navigate to an event
  const goToEvent = (event) => {
    navigation.navigate('EventDetailScreen', {
      id: event.eventId,
      collection: event.collection
    });
  };

  return (
    <View style={styles.card}>
      {/* ... (Team Name, Captain, Invite Code - UNCHANGED) ... */}
      <Text style={styles.teamName}>
        {team.name || 'Untitled Team'}{team.tag ? <Text style={styles.teamTag}>  [{team.tag}]</Text> : null}
      </Text>
      <Text style={styles.muted}>Captain: {captain?.userName || '-'}   -   Members: {members.length}</Text>
      <Text style={styles.muted}>Invite Code: <Text selectable style={styles.code}>{team.inviteCode || '-'}</Text></Text>

      {/* ... (Actions Row - UNCHANGED) ... */}
      <View style={styles.actionsRow}>
        {!iAmOwner && (
          <TouchableOpacity style={[styles.btn, styles.btnSecondary]} onPress={doLeave}>
            <Text style={styles.btnSecondaryText}>Leave Team</Text>
          </TouchableOpacity>
        )}
        {iAmOwner && (
          <>
            <TouchableOpacity style={[styles.btn, styles.btnSecondary]} onPress={doRegenInvite}>
              <Text style={styles.btnSecondaryText}>New Invite Code</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.btn, styles.btnBrand]} onPress={() => setShowTransfer(true)}>
              <Text style={styles.btnBrandText}>Transfer Captain</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.btn, styles.btnDanger]} onPress={() => setShowDisband(true)}>
              <Text style={styles.btnDangerText}>Absolve Team</Text>
            </TouchableOpacity>
          </>
        )}
      </View>

      {/* ... (Disband Warning Box - UNCHANGED) ... */}
       {team.disbandRequested ? (
        <View style={styles.warningBox}>
          <Text style={styles.warningText}>This team is pending disband. Any member may save it.</Text>
          <TouchableOpacity style={[styles.btn, styles.btnPrimary]} onPress={doSave}>
            <Text style={styles.btnPrimaryText}>Save Team</Text>
          </TouchableOpacity>
        </View>
      ) : null}

      {/* Team Stats */}
      <View style={{ height: 14 }} />
      <View style={styles.sectionHeader}>
        <Ionicons name="stats-chart" size={18} color="#FFF" />
        <Text style={styles.sectionTitle}>Team Stats</Text>
      </View>
      <View style={styles.statsRow}>
        <View style={styles.statCard}>
          <Ionicons name="trophy" size={20} color="#8fe2a2" style={{ marginBottom: 4 }} />
          <Text style={styles.statVal}>{stats?.wins || 0}</Text>
          <Text style={styles.statLabel}>Wins</Text>
        </View>
        {critsEnabled ? (
        <View style={styles.statCard}>
          <Ionicons name="dice" size={20} color={GOLD} style={{ marginBottom: 4 }} />
          {/* Crits-on only; earningsCents is legacy-cash only. With Crits off
              the row is Wins / Events / Win Rate. */}
          <Text style={styles.statVal}>{stats?.earningsCrits || 0}</Text>
          <Text style={styles.statLabel}>Crits Won</Text>
        </View>
        ) : null}
        <View style={styles.statCard}>
          <Ionicons name="game-controller" size={20} color="#9146FF" style={{ marginBottom: 4 }} />
          <Text style={styles.statVal}>{stats?.eventsPlayed || 0}</Text>
          <Text style={styles.statLabel}>Events</Text>
        </View>
        <View style={styles.statCard}>
          <Ionicons name="trending-up" size={20} color="#1a5cff" style={{ marginBottom: 4 }} />
          <Text style={styles.statVal}>{stats?.winRate || 0}%</Text>
          <Text style={styles.statLabel}>Win Rate</Text>
        </View>
      </View>

      {/* --- Team Events Section --- */}
      <View style={{ height: 14 }} />
      <View style={styles.sectionHeader}>
        <Ionicons name="calendar" size={18} color="#FFF" />
        <Text style={styles.sectionTitle}>Team Events</Text>
      </View>
      {!teamEvents || teamEvents.length === 0 ? (
        <View style={styles.noEventsBox}>
          <Ionicons name="calendar-outline" size={32} color="#555" />
          <Text style={styles.noEventsText}>No team events yet</Text>
          <Text style={styles.noEventsSubtext}>Browse the Lobby to find team tournaments</Text>
        </View>
      ) : (
        <View>
          {teamEvents.map(event => {
            const when = new Date(event.startAt?.seconds * 1000 || Date.now());
            
            // Status logic with icons
            let statusText, statusColor, statusIcon;
            if (event.status === 'confirmed') {
              statusText = 'CONFIRMED';
              statusColor = '#8fe2a2';
              statusIcon = 'checkmark-circle';
            } else if (event.paidMembers.includes(meUid)) {
              const waitingOn = event.teamSize - event.paidMembers.length;
              statusText = `WAITING FOR ${waitingOn} TEAMMATE${waitingOn > 1 ? 'S' : ''}`;
              statusColor = '#FCD000';
              statusIcon = 'time';
            } else {
              statusText = 'ENTER EVENT';
              statusColor = '#9146FF';
              statusIcon = 'arrow-forward-circle';
            }

            return (
              <TouchableOpacity 
                key={event.eventId} 
                style={styles.eventRow} 
                onPress={() => goToEvent(event)}
              >
                <View style={styles.eventInfo}>
                  <Text style={styles.eventTitle}>{event.name || 'Event'}</Text>
                  <View style={styles.eventMeta}>
                    <Ionicons name="time-outline" size={12} color="#888" />
                    <Text style={styles.eventTime}>{when.toLocaleString()}</Text>
                    {event.teamSize > 1 && (
                      <>
                        <Text style={styles.eventDot}>  </Text>
                        <Ionicons name="people" size={12} color="#888" />
                        <Text style={styles.eventTime}>{event.teamSize}v{event.teamSize}</Text>
                      </>
                    )}
                  </View>
                </View>
                <View style={[styles.eventStatusBadge, { backgroundColor: statusColor + '20', borderColor: statusColor }]}>
                  <Ionicons name={statusIcon} size={14} color={statusColor} />
                  <Text style={[styles.eventStatusText, { color: statusColor }]}>{statusText}</Text>
                </View>
              </TouchableOpacity>
            );
          })}
        </View>
      )}
      {/* --- END: Team Events Section --- */}

      {/* Members List */}
      <View style={{ height: 14 }} />
      <View style={styles.sectionHeader}>
        <Ionicons name="people" size={18} color="#FFF" />
        <Text style={styles.sectionTitle}>Members ({members.length})</Text>
      </View>
      <View>
        {members.map(m => (
          <View key={m.uid} style={styles.memberRow}>
            <View style={styles.memberInfo}>
              <Ionicons 
                name={m.uid === team.ownerId ? 'shield' : 'person'} 
                size={16} 
                color={m.uid === team.ownerId ? GOLD : '#888'} 
                style={{ marginRight: 8 }}
              />
              <Text style={styles.memberName}>
                {m.userName}
                {m.uid === team.ownerId && <Text style={styles.captainBadge}> Captain</Text>}
                {m.uid === meUid && <Text style={styles.youBadge}> You</Text>}
              </Text>
            </View>
            {iAmOwner && m.uid !== team.ownerId && (
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <TouchableOpacity style={[styles.smallBtn, styles.btnSecondary]} onPress={() => doKick(m.uid, m.userName)}>
                  <Text style={styles.btnSecondaryText}>Remove</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[styles.smallBtn, styles.btnSecondary]} onPress={() => {
                  showError('Make Captain', `Make ${m.userName} the captain?`, [
                    { text: 'Cancel', style: 'cancel' },
                    { text: 'Make Captain', onPress: async () => { await transferCaptain(team.id, m.uid); await refresh(); } },
                  ]);
                }}>
                  <Text style={styles.btnSecondaryText}>Make Captain</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        ))}
      </View>

      {/* --- Team memberships (evergreen 3.5b) --- the captain's link to the
          settings screen, with one state line; a roster member gets a
          read-only line. Both link to the public team page. */}
      {tmEntry ? (
        <>
          <View style={{ height: 14 }} />
          {tmEntry.isCaptain === true ? (
            <>
              <TouchableOpacity
                style={styles.tmRow}
                onPress={() => navigation.navigate('CreatorTeamMemberships', { teamId: team.id })}
                accessibilityRole="button"
                accessibilityLabel="Team memberships"
              >
                <Ionicons name="star" size={16} color={GOLD} style={{ marginRight: 10 }} />
                <Text style={styles.tmRowText}>Team memberships</Text>
                <Ionicons name="chevron-forward" size={16} color="#666" />
              </TouchableOpacity>
              <Text style={styles.muted}>{teamMembershipStateLine(tmEntry)}</Text>
            </>
          ) : (
            <>
              <Text style={styles.muted}>{TM_MEMBER_LINE}</Text>
              {teamEverLaunched === true ? <Text style={styles.muted}>{TM_MEMBER_SHARE_LINE}</Text> : null}
            </>
          )}
          <TouchableOpacity
            style={styles.tmLink}
            onPress={() => navigation.navigate('TeamPage', { teamId: team.id })}
            accessibilityRole="link"
            accessibilityLabel="Team page"
          >
            <Text style={styles.tmLinkText}>Team page</Text>
          </TouchableOpacity>
        </>
      ) : null}

      {/* --- Squad Streaming (Team Streams v2) --- squad membership IS this
          roster; the shared panel carries the streaming controls (same UI as
          the Streams page + Streaming Hub). hideTeamLink: we're already on
          the Team tab. key remounts the panel when the roster or captain
          changes so it refetches the squad state. */}
      {squadStreamsVisible ? (
        <>
          <View style={{ height: 14 }} />
          <View style={styles.sectionHeader}>
            <Ionicons name="videocam" size={18} color="#FFF" />
            <Text style={styles.sectionTitle}>Squad Streaming</Text>
          </View>
          <Text style={styles.muted}>
            Stream together as {team.name || 'your team'} - viewers support the whole squad at once.
          </Text>
          <View style={{ height: 8 }} />
          <SquadManagePanel
            key={`${team.id}_${team.ownerId}_${members.length}`}
            navigation={navigation}
            hideTeamLink
            onNavigate={(id) => navigation.navigate('TeamStream', { teamStreamId: id })}
          />
        </>
      ) : null}

      {/* ... (Modals - UNCHANGED) ... */}
      <TransferCaptainModal visible={showTransfer} onClose={() => setShowTransfer(false)} team={team} onDone={refresh} />
      <DisbandTeamModal visible={showDisband} onClose={() => setShowDisband(false)} team={team} onDone={refresh} teamMembershipsShown={tmShown} />
      <ConfirmationModal
        visible={!!confirmAction}
        title={confirmAction?.title}
        message={confirmAction?.message}
        confirmText={confirmAction?.confirmText}
        onCancel={() => setConfirmAction(null)}
        onConfirm={handleConfirm}
        busy={busy}
      />
    </View>
  );
}

// NEW: Helper function to call our new backend function
// We will move this to `teamHelper.js` later, but for a single-file
// patch, we can define it here.
async function loadTeamEvents(teamId) {
  if (!teamId) return [];
  try {
    // Query all entries that have this teamId
    const entriesSnap = await getDocs(
      query(collectionGroup(db, 'entries'), where('teamId', '==', teamId))
    );
    if (entriesSnap.empty) return [];

    const eventsMap = new Map();

    for (const entryDoc of entriesSnap.docs) {
      const entryData = entryDoc.data();
      const eventRef = entryDoc.ref.parent.parent; // (e.g., /tournaments/{eventId})
      
      if (eventRef && !eventsMap.has(eventRef.id)) {
        // Use getDoc from client SDK (not .get() which is Admin SDK)
        const eventSnap = await getDoc(eventRef);
        if (eventSnap.exists()) {
          const eventData = eventSnap.data();
          eventsMap.set(eventRef.id, {
            eventId: eventRef.id,
            collection: eventRef.parent.id, // 'tournaments' or 'matches'
            name: eventData.name,
            startAt: eventData.startAt,
            teamSize: eventData.teamSize,
            // Add the entry data directly
            status: entryData.status, // 'reserved' or 'confirmed'
            paidMembers: entryData.paidMembers || [],
          });
        }
      }
    }

    const events = Array.from(eventsMap.values());
    // Sort by start date, soonest first
    events.sort((a, b) => (a.startAt?.seconds || 0) - (b.startAt?.seconds || 0));
    return events;

  } catch (e) {
    console.error("Error loading team events:", e);
    return [];
  }
}


/* =========================
 * Screen
 * (This component is MODIFIED)
 * ========================= */
export default function YourTeam({ navigation }) {
  const { showError } = useError();
  const [init, setInit] = useState(true);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState(null);
  const [stats, setStats] = useState(null);
  const [nextEvents, setNextEvents] = useState([]); // Kept for `loadTeamBundle`
  // NEW: State for our team-specific event list
  const [teamEvents, setTeamEvents] = useState([]);
  // Team memberships (evergreen 3.5b): getMyStreamInfo.teamMembershipEntry,
  // read once per refresh after loadTeamBundle finds a team. null = no row
  // (failure, the direct-query fallback, an older server, or no team).
  const [teamMembershipEntry, setTeamMembershipEntry] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => { setMe(u || null); setInit(false); });
    return () => unsub();
  }, []);

  // MODIFIED: `refresh` function now also loads team events
  const refresh = useCallback(async () => {
    if (!me?.uid) { 
      setTeam(null); 
      setStats(null); 
      setNextEvents([]); 
      setTeamEvents([]);
      setTeamMembershipEntry(null);
      setLoading(false); 
      return; 
    }
    setLoading(true);
    
    let t = null;
    let bundleStats = null;
    let bundleNextEvents = [];
    let tmEntry = null;
    
    // Try loadTeamBundle first
    try {
      const bundle = await loadTeamBundle(db);
      t = bundle.team;
      bundleStats = bundle.stats || null;
      bundleNextEvents = bundle.nextEvents || [];
      // Team memberships entry (evergreen 3.5b): its own try, so a failure
      // only hides the row (never the team).
      if (t) {
        try {
          const info = await httpsCallable(functions, 'getMyStreamInfo')();
          const e = info?.data?.teamMembershipEntry;
          tmEntry = e && typeof e === 'object' && e.teamId === t.id ? e : null;
        } catch (infoErr) {
          console.warn('getMyStreamInfo failed (no team memberships row):', infoErr?.message);
          tmEntry = null;
        }
      }
    } catch (bundleErr) {
      console.warn('loadTeamBundle failed, trying direct query:', bundleErr.message);
      
      // Fallback: Query Firestore directly for user's team
      try {
        const teamsQuery = query(
          collection(db, 'teams'),
          where('memberIds', 'array-contains', me.uid),
          limit(1)
        );
        const teamsSnap = await getDocs(teamsQuery);
        
        if (!teamsSnap.empty) {
          const teamDoc = teamsSnap.docs[0];
          const teamData = teamDoc.data();
          
          // Build member profiles from memberIds
          const memberProfiles = [];
          for (const memberId of (teamData.memberIds || [])) {
            try {
              const profileSnap = await getDoc(doc(db, 'public_profiles', memberId));
              const userData = profileSnap.exists() ? profileSnap.data() : {};
              memberProfiles.push({
                uid: memberId,
                userName: userData.userName || userData.displayName || 'Unknown',
              });
            } catch (e) {
              memberProfiles.push({ uid: memberId, userName: 'Unknown' });
            }
          }
          
          t = {
            id: teamDoc.id,
            ...teamData,
            memberProfiles,
          };
        }
      } catch (fallbackErr) {
        console.error('Fallback team query also failed:', fallbackErr);
      }
    }
    
    if (t) {
      t.meUid = me.uid;
      setTeam(t);
      setStats(bundleStats);
      setNextEvents(bundleNextEvents);
      setTeamMembershipEntry(tmEntry);

      // Load team events separately
      try {
        const events = await loadTeamEvents(t.id);
        setTeamEvents(events);
      } catch (eventsErr) {
        console.warn('Failed to load team events:', eventsErr);
        setTeamEvents([]);
      }

    } else {
      setTeam(null); 
      setStats(null); 
      setNextEvents([]);
      setTeamEvents([]);
      setTeamMembershipEntry(null);
    }
    
    setLoading(false);
  }, [me?.uid]);

  useEffect(() => { if (!init) refresh(); }, [init, refresh]);

  const canCreateOrJoin = !team;

  if (init || loading) {
    return (<View style={styles.loadingWrap}><ActivityIndicator size="large" color={GOLD} /></View>);
  }

  // ... (Login check is UNCHANGED) ...
  if (!me) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: '#0B0B0B' }}>
        <Header navigation={navigation} />
        <View style={styles.centerBox}>
          <Text style={styles.title}>Your Team</Text>
          <Text style={styles.muted}>Please log in to manage teams.</Text>
          <TouchableOpacity style={[styles.btn, styles.btnPrimary]} onPress={() => navigation.replace('Login')}>
            <Text style={styles.btnPrimaryText}>Go to Login</Text>
          </TouchableOpacity>
        </View>
        <Footer navigation={navigation} />
        <NavBar navigation={navigation} />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: '#0B0B0B' }}>
      <Header navigation={navigation} />
      <ScrollView contentContainerStyle={styles.container}>
        <Text style={styles.title}>Your Team</Text>

        {/* This "No Team" card is UNCHANGED and keeps your create/join UX */}
        {!team ? (
          <View style={styles.card}>
            <View style={styles.noTeamHeader}>
              <Ionicons name="people" size={24} color={GOLD} />
              <Text style={styles.noTeamTitle}>Join or Create a Team</Text>
            </View>
            <Text style={styles.muted}>
              Form a squad for team-based events like Rainbow Six Siege, Rocket League, and NBA 2K Park modes.
            </Text>
            <View style={styles.actionsRow}>
              <TouchableOpacity
                style={[styles.btn, styles.btnPrimary]}
                onPress={() => {
                  if (!canCreateOrJoin) return showError('Already on a team', 'Leave your current team first.');
                  setCreateOpen(true);
                }}
              >
                <Text style={styles.btnPrimaryText}>Create a Team</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.btn, styles.btnSecondary]}
                onPress={() => {
                  if (!canCreateOrJoin) return showError('Already on a team', 'Leave your current team first.');
                  setJoinOpen(true);
                }}
              >
                <Text style={styles.btnSecondaryText}>Join with Code</Text>
              </TouchableOpacity>
            </View>
            
            {/* Pickup Groups Info */}
            <View style={styles.pickupInfoBox}>
              <View style={styles.pickupInfoHeader}>
                <Ionicons name="shuffle" size={18} color="#9146FF" />
                <Text style={styles.pickupInfoTitle}>Don't have a team?</Text>
              </View>
              <Text style={styles.pickupInfoText}>
                You can still enter team events solo! Pickup groups are automatically formed from solo players at event start. Pre-formed teams get priority seeding.
              </Text>
            </View>
            
            <View style={styles.hRule} />
            <Text style={styles.sectionTitle}>Team Benefits</Text>
            <View style={styles.benefitsList}>
              <View style={styles.benefitRow}>
                <Ionicons name="trophy" size={16} color="#8fe2a2" />
                <Text style={styles.benefitText}>Priority matching with teammates in events</Text>
              </View>
              <View style={styles.benefitRow}>
                <Ionicons name="ribbon" size={16} color="#FCD000" />
                <Text style={styles.benefitText}>Track team stats together</Text>
              </View>
              <View style={styles.benefitRow}>
                <Ionicons name="link" size={16} color="#9146FF" />
                <Text style={styles.benefitText}>Share invite code to add members</Text>
              </View>
              <View style={styles.benefitRow}>
                <Ionicons name="shield-checkmark" size={16} color="#1a5cff" />
                <Text style={styles.benefitText}>Better seeding vs pickup groups</Text>
              </View>
            </View>
          </View>
        ) : (
          // MODIFIED: Pass 'teamEvents' to the card
          <TeamCard 
            team={team} 
            meUid={me.uid} 
            refresh={refresh} 
            stats={stats} 
            nextEvents={nextEvents}
            teamEvents={teamEvents} 
            teamMembershipEntry={teamMembershipEntry}
          />
        )}
      </ScrollView>
      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />

      {/* These Modals are UNCHANGED */}
      <CreateTeamModal visible={createOpen} onClose={() => setCreateOpen(false)} onDone={refresh} />
      <JoinTeamModal visible={joinOpen} onClose={() => setJoinOpen(false)} onDone={refresh} />

      {/* Solo-or-squad detection (self-gating: flag + auth + something to
          suggest; at most once per mode per session). */}
      <SquadLinkPrompt />
    </SafeAreaView>
  );
}

/* =========================
 * Styles
 * (MODIFIED)
 * ========================= */
const styles = StyleSheet.create({
  container: { padding: 16, gap: 16 },
  loadingWrap: { flex: 1, backgroundColor: '#0B0B0B', alignItems: 'center', justifyContent: 'center' },
  title: { color: '#FFF', fontSize: 22, fontWeight: '700', marginBottom: 8 },
  card: { backgroundColor: '#121212', borderRadius: 14, padding: 16, borderWidth: 1, borderColor: '#2A2A2A' },
  teamName: { color: '#FFF', fontSize: 18, fontWeight: '700' },
  teamTag: { color: GOLD, fontSize: 16, fontWeight: '800' },
  muted: { color: '#A9A9A9', marginTop: 4, lineHeight: 20 },
  code: { color: '#FFF', fontWeight: '700' },
  actionsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginTop: 12 },
  warningBox: { marginTop: 12, padding: 12, borderRadius: 12, backgroundColor: '#2b1a00', borderColor: '#553400', borderWidth: 1 },
  warningText: { color: '#FFD899', marginBottom: 8 },
  
  // Section headers
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  sectionTitle: { color: '#FFF', fontWeight: '700', marginTop: 0, marginBottom: 0 },
  
  // Stats
  statsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
  statCard: { flexGrow: 1, minWidth: 80, backgroundColor: '#0E0E0E', borderRadius: 12, borderWidth: 1, borderColor: '#252525', padding: 12, alignItems: 'center' },
  statVal: { color: '#FFF', fontSize: 18, fontWeight: '800' },
  statLabel: { color: '#B5B5B5', marginTop: 4, fontSize: 12 },

  // Event rows
  eventRow: { 
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12, 
    borderBottomWidth: StyleSheet.hairlineWidth, 
    borderBottomColor: '#222' 
  },
  eventInfo: {
    flex: 1,
  },
  eventTitle: { color: '#EDEDED', fontSize: 15, fontWeight: '700' },
  eventMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
    gap: 4,
  },
  eventTime: { color: '#888', fontSize: 12 },
  eventDot: { color: '#555' },
  eventStatusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 6,
    borderWidth: 1,
    marginLeft: 8,
  },
  eventStatusText: {
    fontSize: 10,
    fontWeight: '800',
  },
  
  // No events state
  noEventsBox: {
    alignItems: 'center',
    paddingVertical: 24,
    backgroundColor: '#0E0E0E',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#252525',
  },
  noEventsText: {
    color: '#888',
    fontSize: 14,
    fontWeight: '600',
    marginTop: 8,
  },
  noEventsSubtext: {
    color: '#555',
    fontSize: 12,
    marginTop: 4,
  },

  // Member rows
  memberRow: { paddingVertical: 10, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: '#222', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  memberInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  memberName: { color: '#EDEDED', fontSize: 15 },
  captainBadge: { color: GOLD, fontWeight: '700', fontSize: 12 },
  youBadge: { color: '#888', fontSize: 12 },
  
  // Buttons
  btn: { paddingVertical: 10, paddingHorizontal: 14, borderRadius: 10, minWidth: 120, alignItems: 'center' },
  smallBtn: { paddingVertical: 6, paddingHorizontal: 10, borderRadius: 8, minWidth: 0 },
  btnPrimary: { backgroundColor: GOLD },
  btnPrimaryText: { color: '#141414', fontWeight: '800' },
  btnSecondary: { borderWidth: 1, borderColor: '#404040' },
  btnSecondaryText: { color: '#EDEDED', fontWeight: '700' },
  btnDanger: { backgroundColor: '#381010', borderWidth: 1, borderColor: '#6b1f1f' },
  btnDangerText: { color: '#ffb3b3', fontWeight: '800' },
  btnBrand: { borderWidth: 1, borderColor: GOLD, backgroundColor: 'transparent' },
  btnBrandText: { color: GOLD, fontWeight: '800' },
  
  // Team memberships row (evergreen 3.5b; the Streaming Hub row's shape)
  tmRow: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#1a1a1a', borderRadius: 8, paddingHorizontal: 16, paddingVertical: 14 },
  tmRowText: { flex: 1, color: '#FFF', fontSize: 14, fontWeight: '600' },
  tmLink: { alignSelf: 'flex-start', minHeight: 36, justifyContent: 'center', paddingVertical: 6 },
  tmLinkText: { color: GOLD, fontWeight: '700' },

  // Modal
  modalBackdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.7)', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modalCard: { width: '100%', maxWidth: 520, backgroundColor: '#0E0E0E', borderRadius: 14, borderWidth: 1, borderColor: '#252525', padding: 16 },
  modalTitle: { color: '#FFF', fontWeight: '800', fontSize: 18, marginBottom: 8 },
  input: { backgroundColor: '#151515', color: '#FFF', borderWidth: 1, borderColor: '#2A2A2A', borderRadius: 10, paddingHorizontal: 12, paddingVertical: 10, marginTop: 8 },
  rowGap: { flexDirection: 'row', gap: 10, marginTop: 12 },
  centerBox: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24, gap: 14 },
  hRule: { height: 1, backgroundColor: '#232323', marginVertical: 12 },
  memberList: { maxHeight: 280, marginTop: 6, gap: 8 },
  memberOption: { paddingVertical: 12, paddingHorizontal: 12, borderRadius: 10, borderWidth: 1, borderColor: '#2A2A2A', backgroundColor: '#141414' },
  memberOptionOn: { borderColor: GOLD, backgroundColor: '#1a1600' },
  memberText: { color: '#F1F1F1', fontSize: 16, fontWeight: '600' },
  memberTextOn: { color: GOLD, fontWeight: '800' },

  // No Team card styles
  noTeamHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 8,
  },
  noTeamTitle: {
    color: '#FFF',
    fontSize: 18,
    fontWeight: '700',
  },
  
  // Pickup info box
  pickupInfoBox: {
    backgroundColor: '#1a1a2e',
    borderRadius: 10,
    padding: 12,
    marginTop: 12,
    borderWidth: 1,
    borderColor: '#9146FF30',
  },
  pickupInfoHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 6,
  },
  pickupInfoTitle: {
    color: '#9146FF',
    fontSize: 14,
    fontWeight: '700',
  },
  pickupInfoText: {
    color: '#8a8fa8',
    fontSize: 12,
    lineHeight: 18,
  },
  
  // Benefits list
  benefitsList: {
    gap: 8,
  },
  benefitRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  benefitText: {
    color: '#A9A9A9',
    fontSize: 13,
    flex: 1,
  },
});